/**
 * rebalanceTarget — a portfolio's target allocation and its drift bars (WORK.md item 45, D21).
 *
 * Stored on the portfolio's own entry in settings (`portfolios[].rebalanceTarget`,
 * `portfolios[].rebalanceThresholds`), so it syncs like every other portfolio
 * property. This module reads, writes and copies; it judges nothing — the
 * verdict is `/projection-drift`'s (ADR-0031), and the defaults below are the
 * request schema's own, repeated here only so the settings form can show them.
 */
import type { PortfolioRef, RebalanceTarget, Settings } from './portfolioApi';
import { updateSettings } from './settingsStore';
import type { Aim, ModelRef } from './modelSettings';

/** The owner's defaults (D21) — python-compute `ProjectionDriftRequest`'s. */
export const DRIFT_DEFAULTS = Object.freeze({
  returnThreshold: 0.03,
  confidence: 0.7,
  riskThreshold: 0.02,
});

export type ResolvedThresholds = { returnThreshold: number; confidence: number; riskThreshold: number };

function portfolioOf(settings: Settings, portfolioId: string): PortfolioRef | undefined {
  return (settings.portfolios ?? []).find((p) => p.id === portfolioId);
}

/** The portfolio's target, or null when none is set or it holds no positive weight. */
export function resolveRebalanceTarget(settings: Settings, portfolioId: string): RebalanceTarget | null {
  const t = portfolioOf(settings, portfolioId)?.rebalanceTarget;
  if (!t || !Object.values(t.weights ?? {}).some((w) => w > 0)) return null;
  return t;
}

export function resolveRebalanceThresholds(settings: Settings, portfolioId: string): ResolvedThresholds {
  const t = portfolioOf(settings, portfolioId)?.rebalanceThresholds ?? {};
  const pick = (v: number | undefined, d: number) => (v != null && Number.isFinite(v) ? v : d);
  return {
    returnThreshold: pick(t.returnThreshold, DRIFT_DEFAULTS.returnThreshold),
    confidence: pick(t.confidence, DRIFT_DEFAULTS.confidence),
    riskThreshold: pick(t.riskThreshold, DRIFT_DEFAULTS.riskThreshold),
  };
}

/**
 * The target a one-click copy produces: the given weights with non-positive
 * entries dropped. Not renormalised here — the endpoint normalises the target
 * over the names it can measure, and says which it could not.
 */
export function targetFrom(
  weights: Record<string, number>,
  source: RebalanceTarget['source'],
  now: Date = new Date(),
): RebalanceTarget {
  const kept = Object.fromEntries(Object.entries(weights).filter(([, w]) => Number.isFinite(w) && w > 0));
  return { weights: kept, source, setAt: now.toISOString() };
}

/**
 * Weights of the INVESTED book from priced rows — the same basis
 * useProjectionRun sends as `holdings` (cash excluded, unpriced rows left
 * out), so "copy from holdings" copies exactly the book the check measures.
 */
export function investedWeights(rows: { ticker: string; marketValue: number | null }[]): Record<string, number> {
  const priced = rows.filter((r) => (r.marketValue ?? 0) > 0);
  const total = priced.reduce((s, r) => s + (r.marketValue as number), 0);
  if (total <= 0) return {};
  return Object.fromEntries(priced.map((r) => [r.ticker, (r.marketValue as number) / total]));
}

type PortfolioPatch = Partial<Pick<PortfolioRef, 'rebalanceTarget' | 'rebalanceThresholds'>>;

/** Why a target could not be stored: the portfolio has no settings entry. */
export class NoPortfolioEntryError extends Error {
  constructor(portfolioId: string) {
    super(
      `Portfolio "${portfolioId}" has no entry in settings, so nothing can be stored for it. `
      + 'Give it a name under Portfolios first; that creates its entry.',
    );
    this.name = 'NoPortfolioEntryError';
  }
}

/**
 * Write the target and/or thresholds onto one portfolio, fresh-read-merged
 * through settingsStore so no other field is clobbered. A key set to
 * undefined is removed.
 *
 * REFUSES rather than no-ops when the portfolio has no `settings.portfolios`
 * entry (the legacy `default` book can have none): mapping over the list
 * would change nothing and save it back, and the card would say "saved" for
 * a target stored nowhere.
 */
export function saveRebalanceSettings(portfolioId: string, patch: PortfolioPatch): Promise<Settings> {
  return updateSettings((s) => {
    if (!(s.portfolios ?? []).some((p) => p.id === portfolioId)) {
      throw new NoPortfolioEntryError(portfolioId);
    }
    return {
      ...s,
      portfolios: (s.portfolios ?? []).map((p) => {
        if (p.id !== portfolioId) return p;
        const next: PortfolioRef = { ...p, ...patch };
        for (const k of Object.keys(patch) as (keyof PortfolioPatch)[]) {
          if (patch[k] === undefined) delete next[k];
        }
        return next;
      }),
    };
  });
}

/**
 * Write the chosen model and/or aim onto one portfolio's model settings
 * (item 57, `portfolios[].modelSettings`), keeping every other field the
 * block holds. The block is partial, so a field not written here still falls
 * back to the person's default and the built-in one (`resolveModelSettings`);
 * the edge's write guard refuses a block out of bounds and writes nothing.
 * Refuses, as `saveRebalanceSettings` does, when the portfolio has no entry.
 */
export function saveModelChoice(
  portfolioId: string,
  patch: { model?: ModelRef | null; aim?: Aim },
): Promise<Settings> {
  return updateSettings((s) => {
    if (!(s.portfolios ?? []).some((p) => p.id === portfolioId)) {
      throw new NoPortfolioEntryError(portfolioId);
    }
    return {
      ...s,
      portfolios: (s.portfolios ?? []).map((p) => {
        if (p.id !== portfolioId) return p;
        const cur = p.modelSettings != null && typeof p.modelSettings === 'object' && !Array.isArray(p.modelSettings)
          ? p.modelSettings as Record<string, unknown>
          : {};
        return { ...p, modelSettings: { ...cur, ...patch } };
      }),
    };
  });
}
